import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import NotificationCenter from '../components/NotificationCenter';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { uploadSpeakerImage } from '../services/api';
import { formatImageUrl } from '../utils/imageUtils';

interface Presentation {
  id: string;
  title: string;
  abstract: string;
  speaker_name: string;
  speaker_bio: string;
  email: string;
  organization?: string;
  presentation_type: 'talk' | 'workshop' | 'panel';
  status: 'pending' | 'accepted' | 'rejected' | 'scheduled' | 'approved_scheduled' | 'approved_unscheduled';
  scheduled_time?: string;
  scheduled_room?: string;
  feedback?: Array<{
    id: string;
    comment: string;
    timestamp: string;
    reviewer: string;
  }>;
}

interface SpeakerProfile {
  id: number;
  full_name: string;
  bio: string;
  photo_url?: string;
  image?: string;
  twitter_handle?: string;
  linkedin_url?: string;
  organization?: string;
}

interface NewProposalFormData {
  title: string;
  abstract: string;
  presentation_type: 'talk' | 'workshop' | 'panel';
}

const SpeakerDashboard: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<NewProposalFormData>({
    title: '',
    abstract: '',
    presentation_type: 'talk'
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | undefined>(
    localStorage.getItem('speakerImageUrl') || undefined
  );

  const { data: presentations, isLoading: presentationsLoading } = useQuery({
    queryKey: ['presentations'],
    queryFn: async () => {
      if (!user?.email) {
        console.error('No user email available:', user);
        throw new Error('User email not available');
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      // Add the authorization token if available
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/speaker/presentations`, {
        credentials: 'include',
        headers
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Presentation fetch error:', error);
        throw new Error(error.detail || 'Failed to fetch presentations');
      }
      return response.json();
    },
    enabled: !!user?.email
  });

  // Fetch speaker profile information
  const { data: speakerProfile, isLoading: profileLoading } = useQuery({
    queryKey: ['speakerProfile'],
    queryFn: async () => {
      if (!user?.email) {
        throw new Error('User email not available');
      }

      // Use the first presentation to get speaker bio and organization
      if (presentations && presentations.length > 0) {
        const firstPresentation = presentations[0];
        return {
          bio: firstPresentation.speaker_bio,
          full_name: firstPresentation.speaker_name,
          photo_url: undefined, // Will be updated after upload
          organization: firstPresentation.organization || 'Language Intelligence Conference'
        } as SpeakerProfile;
      }

      // Fallback values if no presentations exist
      return {
        id: 0,
        bio: 'Experienced speaker at the Language Intelligence Conference with expertise in natural language processing, machine learning, and artificial intelligence. Passionate about advancing the field of language intelligence through innovative research and practical applications.',
        full_name: user?.full_name || 'Speaker',
        photo_url: undefined,
        organization: 'Language Intelligence Conference'
      } as SpeakerProfile;
    },
    enabled: !!user?.email && !!presentations
  });

  const submitProposalMutation = useMutation({
    mutationFn: async (data: NewProposalFormData) => {
      if (!user?.email) {
        throw new Error('User email not available');
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      // Add the authorization token if available
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Create the complete submission data with user information
      const submissionData = {
        ...data,
        speaker_name: user.full_name || 'Unknown',
        speaker_bio: speakerProfile?.bio || 'Experienced speaker at the Language Intelligence Conference with expertise in natural language processing, machine learning, and artificial intelligence. Passionate about advancing the field of language intelligence through innovative research and practical applications.',
        email: user.email,
        organization: speakerProfile?.organization || 'Language Intelligence Conference'
      };

      const response = await fetch(`${API_BASE_URL}/cfp/submit`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        const error = await response.json();
        if (error.detail && Array.isArray(error.detail)) {
          // Handle validation errors
          const validationErrors = error.detail.map((err: any) => 
            `${err.loc[1]}: ${err.msg}`
          ).join('\n');
          throw new Error(validationErrors);
        }
        throw new Error(error.detail || 'Failed to submit proposal');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['presentations'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      setFormError(error.message);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    // Validate form
    if (!formData.title.trim()) {
      setFormError('Title is required');
      return;
    }
    if (!formData.abstract.trim()) {
      setFormError('Abstract is required');
      return;
    }
    
    submitProposalMutation.mutate(formData);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      abstract: '',
      presentation_type: 'talk'
    });
    setFormError(null);
  };

  const openModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Handle image upload
  const handleImageUpload = async (file: File) => {
    try {
      setIsImageUploading(true);
      const result = await uploadSpeakerImage(file);
      
      if (result.success) {
        // Store the uploaded image URL in state
        setUploadedImageUrl(result.image_url);
        localStorage.setItem('speakerImageUrl', result.image_url);
        
        // Update the local state with the new image URL
        queryClient.invalidateQueries({ queryKey: ['speakerProfile'] });
        
        // Also directly update the speaker profile state
        if (speakerProfile) {
          queryClient.setQueryData(['speakerProfile'], {
            ...speakerProfile,
            image: result.image_url,
            photo_url: result.image_url
          });
        }
        
        // Show success message
        alert('Profile photo uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsImageUploading(false);
    }
  };

  useEffect(() => {
    if (speakerProfile) {
      console.log('Speaker profile updated:', speakerProfile);
    }
  }, [speakerProfile]);

  useEffect(() => {
    console.log('Uploaded image URL updated:', uploadedImageUrl);
    
    // Persist the image URL to localStorage
    if (uploadedImageUrl) {
      localStorage.setItem('speakerImageUrl', uploadedImageUrl);
    }
  }, [uploadedImageUrl]);

  if (presentationsLoading || profileLoading) {
    return <div className="min-h-screen bg-gray-100 p-6 flex items-center justify-center">
      <div className="text-xl">Loading...</div>
    </div>;
  }

  // Count scheduled presentations
  const scheduledPresentations = presentations?.filter(
    (p: Presentation) => p.status === 'scheduled' || p.status === 'approved_scheduled'
  ).length || 0;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Blue header */}
      <div className="bg-blue-700 text-white p-6 pt-20">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Welcome, {user?.full_name || 'Speaker'}</h1>
            <p className="text-blue-100">Manage your conference presentations and preferences</p>
          </div>
          <Link to="/" className="text-white flex items-center hover:underline">
            <span className="mr-2">Back to Main Page</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
            </svg>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 pt-20">
        {/* Main content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Presentations Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h2 className="text-xl font-semibold text-gray-800">Presentations</h2>
            </div>
            <p className="text-gray-600 mb-4">View and manage your conference presentations</p>
            <div className="flex justify-between items-center">
              <Link to="/presentations" className="text-blue-600 flex items-center hover:text-blue-800">
                Learn more
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </Link>
              <button 
                onClick={openModal} 
                className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-sm"
              >
                Submit New Proposal
              </button>
            </div>
          </div>

          {/* Schedule Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h2 className="text-xl font-semibold text-gray-800">Schedule</h2>
            </div>
            <p className="text-gray-600 mb-4">View the conference schedule and your sessions</p>
            <Link to="/schedule" className="text-blue-600 flex items-center hover:text-blue-800">
              Learn more
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>

          {/* Resources Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <h2 className="text-xl font-semibold text-gray-800">Resources</h2>
            </div>
            <p className="text-gray-600 mb-4">Access conference materials and documents</p>
            <Link to="/resources" className="text-blue-600 flex items-center hover:text-blue-800">
              Learn more
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>

          {/* Notifications Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <h2 className="text-xl font-semibold text-gray-800">Notifications</h2>
            </div>
            <p className="text-gray-600 mb-4">View important updates and announcements</p>
            <Link to="/notifications" className="text-blue-600 flex items-center hover:text-blue-800">
              Learn more
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Profile Photo Section */}
        <div className="mb-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Profile Photo</h2>
          <div className="flex items-center space-x-6">
            <div className="flex-shrink-0">
              <img 
                src={formatImageUrl(uploadedImageUrl || speakerProfile?.photo_url || speakerProfile?.image, '/placeholder-speaker.svg')} 
                alt={speakerProfile?.full_name || user?.full_name} 
                className="w-32 h-32 rounded-full object-cover border-2 border-blue-500"
              />
            </div>
            <div>
              <p className="text-gray-600 mb-2">
                Upload a professional photo to display on the conference website.
                Recommended size: 400x400 pixels.
              </p>
              <input 
                type="file" 
                accept="image/*" 
                className="hidden" 
                ref={fileInputRef}
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    handleImageUpload(files[0]);
                  }
                }}
              />
              <button 
                onClick={() => fileInputRef.current?.click()}
                disabled={isImageUploading}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
              >
                {isImageUploading ? 'Uploading...' : 'Upload New Photo'}
              </button>
            </div>
          </div>
        </div>

        {/* Quick Overview Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Quick Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Scheduled Sessions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-gray-600 mb-2">Scheduled Sessions</h3>
              <p className="text-3xl font-bold text-blue-600">{scheduledPresentations}</p>
            </div>

            {/* Pending Submissions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-gray-600 mb-2">Pending Submissions</h3>
              <p className="text-3xl font-bold text-blue-600">
                {presentations?.filter((p: Presentation) => p.status === 'pending').length || 0}
              </p>
            </div>

            {/* Available Resources */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-gray-600 mb-2">Available Resources</h3>
              <p className="text-3xl font-bold text-blue-600">5</p>
            </div>
          </div>
        </div>

        {/* Presentations List (Collapsed by default) */}
        <div className="bg-white rounded-lg shadow overflow-hidden mb-8">
          <div className="p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-800">My Presentations</h2>
          </div>
          <div className="divide-y">
            {presentations?.length === 0 ? (
              <div className="p-6">
                <p className="text-gray-600">No presentations yet. Submit a proposal through the Call for Speakers form.</p>
                <div className="mt-4">
                  <button 
                    onClick={openModal}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 inline-block"
                  >
                    Submit New Proposal
                  </button>
                </div>
              </div>
            ) : (
              presentations?.map((presentation: Presentation) => (
                <div key={presentation.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-gray-800">{presentation.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{presentation.abstract.substring(0, 100)}...</p>
                      
                      {(presentation.status === 'scheduled' || presentation.status === 'approved_scheduled') && presentation.scheduled_time && (
                        <div className="mt-2 text-sm text-gray-600">
                          <p>Scheduled Time: {new Date(presentation.scheduled_time).toLocaleString()}</p>
                          <p>Room: {presentation.scheduled_room || 'TBA'}</p>
                        </div>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      presentation.status === 'accepted' || presentation.status === 'approved_unscheduled' ? 'bg-green-100 text-green-800' :
                      presentation.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      presentation.status === 'scheduled' || presentation.status === 'approved_scheduled' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {presentation.status === 'approved_scheduled' ? 'Scheduled' :
                       presentation.status === 'approved_unscheduled' ? 'Accepted' :
                       presentation.status.charAt(0).toUpperCase() + presentation.status.slice(1)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
          {presentations?.length > 0 && (
            <div className="p-6 border-t">
              <button 
                onClick={openModal}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 inline-block"
              >
                Submit New Proposal
              </button>
            </div>
          )}
        </div>
      </div>

      {/* New Proposal Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Submit New Proposal</h2>
              <button 
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              {formError && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                  {formError.split('\n').map((error, index) => (
                    <div key={index} className="mb-1">
                      {error}
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mb-4">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="abstract" className="block text-sm font-medium text-gray-700 mb-1">
                  Abstract *
                </label>
                <textarea
                  id="abstract"
                  name="abstract"
                  value={formData.abstract}
                  onChange={handleInputChange}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                ></textarea>
              </div>
              
              <div className="mb-6">
                <label htmlFor="presentation_type" className="block text-sm font-medium text-gray-700 mb-1">
                  Presentation Type *
                </label>
                <select
                  id="presentation_type"
                  name="presentation_type"
                  value={formData.presentation_type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="talk">Talk</option>
                  <option value="workshop">Workshop</option>
                  <option value="panel">Panel</option>
                </select>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  disabled={submitProposalMutation.isPending}
                >
                  {submitProposalMutation.isPending ? 'Submitting...' : 'Submit Proposal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpeakerDashboard;
